import React, { useState, useEffect } from 'react';
import { useContractStore } from '../../stores/contractStore';
import { ContractTable } from '../contracts/ContractTable';
import { ArrowDown, ArrowUp, Building2, DollarSign, Calendar, MapPin, ExternalLink, Sparkles } from 'lucide-react';
import { cn } from '../../utils/cn';

interface SortConfig {
  key: string;
  direction: 'asc' | 'desc';
}

export function OpportunitiesSection() {
  const { contracts, loading, error, fetchContracts } = useContractStore();
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: '', direction: 'asc' });
  const [hoveredContract, setHoveredContract] = useState<string | null>(null);

  useEffect(() => {
    fetchContracts();
  }, [fetchContracts]);

  const handleSort = (key: string) => {
    setSortConfig((currentSort) => ({
      key,
      direction:
        currentSort.key === key && currentSort.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const sortedContracts = [...contracts].sort((a, b) => {
    if (!sortConfig.key) return 0;

    let aValue = a[sortConfig.key as keyof typeof a];
    let bValue = b[sortConfig.key as keyof typeof b];

    if (sortConfig.key === 'value') {
      aValue = a.value || 0;
      bValue = b.value || 0;
    }

    if (sortConfig.key === 'deadline') {
      aValue = new Date(a.deadlineDate).getTime();
      bValue = new Date(b.deadlineDate).getTime();
    }

    if (sortConfig.direction === 'asc') {
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    } else {
      return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
    }
  });

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-indigo-600 border-t-transparent"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg bg-red-50 p-4">
        <div className="flex">
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error</h3>
            <div className="mt-2 text-sm text-red-700">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow">
        {/* Table Header */}
        <div className="grid grid-cols-12 gap-4 border-b border-gray-200 bg-gray-50 px-4 py-3.5 text-left text-sm font-semibold text-gray-900 sm:px-6">
          <div
            className="col-span-4 sm:col-span-3 flex cursor-pointer items-center"
            onClick={() => handleSort('title')}
          >
            <Building2 className="mr-2 h-4 w-4 text-gray-400" />
            Contract
            {sortConfig.key === 'title' && (
              sortConfig.direction === 'asc' ? <ArrowUp className="ml-1 h-4 w-4" /> : <ArrowDown className="ml-1 h-4 w-4" />
            )}
          </div>
          <div
            className="col-span-3 hidden cursor-pointer items-center sm:flex"
            onClick={() => handleSort('agency')}
          >
            Agency
            {sortConfig.key === 'agency' && (
              sortConfig.direction === 'asc' ? <ArrowUp className="ml-1 h-4 w-4" /> : <ArrowDown className="ml-1 h-4 w-4" />
            )}
          </div>
          <div
            className="col-span-3 sm:col-span-2 flex cursor-pointer items-center"
            onClick={() => handleSort('value')}
          >
            <DollarSign className="mr-2 h-4 w-4 text-gray-400" />
            Value
            {sortConfig.key === 'value' && (
              sortConfig.direction === 'asc' ? <ArrowUp className="ml-1 h-4 w-4" /> : <ArrowDown className="ml-1 h-4 w-4" />
            )}
          </div>
          <div
            className="col-span-3 sm:col-span-2 flex cursor-pointer items-center"
            onClick={() => handleSort('deadline')}
          >
            <Calendar className="mr-2 h-4 w-4 text-gray-400" />
            Deadline
            {sortConfig.key === 'deadline' && (
              sortConfig.direction === 'asc' ? <ArrowUp className="ml-1 h-4 w-4" /> : <ArrowDown className="ml-1 h-4 w-4" />
            )}
          </div>
          <div
            className="col-span-2 flex cursor-pointer items-center"
            onClick={() => handleSort('status')}
          >
            Status
            {sortConfig.key === 'status' && (
              sortConfig.direction === 'asc' ? <ArrowUp className="ml-1 h-4 w-4" /> : <ArrowDown className="ml-1 h-4 w-4" />
            )}
          </div>
        </div>

        {/* Table Body */}
        <div className="divide-y divide-gray-200">
          {sortedContracts.map((contract) => (
            <div
              key={contract.id}
              className={cn(
                "grid grid-cols-12 gap-4 px-4 py-4 transition-all hover:bg-gray-50 sm:px-6",
                hoveredContract === contract.id && "bg-gray-50"
              )}
              onMouseEnter={() => setHoveredContract(contract.id)}
              onMouseLeave={() => setHoveredContract(null)}
            >
              <div className="col-span-4 sm:col-span-3">
                <div className="flex flex-col">
                  <span className="font-medium text-gray-900">{contract.title}</span>
                  <span className="mt-1 flex items-center text-sm text-gray-500">
                    <MapPin className="mr-1.5 h-4 w-4 flex-shrink-0 text-gray-400" />
                    {contract.location || 'Location not specified'}
                  </span>
                </div>
              </div>
              <div className="col-span-3 hidden sm:block">
                <span className="text-gray-900">{contract.agency}</span>
                {contract.setAside && (
                  <span className="mt-1 inline-flex rounded-full bg-blue-100 px-2 text-xs font-semibold text-blue-800">
                    {contract.setAside}
                  </span>
                )}
              </div>
              <div className="col-span-3 sm:col-span-2">
                <span className="text-gray-900">
                  {contract.value
                    ? new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'USD',
                        minimumFractionDigits: 0,
                      }).format(contract.value)
                    : 'Not specified'}
                </span>
              </div>
              <div className="col-span-3 sm:col-span-2">
                <span className="text-gray-900">
                  {new Date(contract.deadlineDate).toLocaleDateString()}
                </span>
              </div>
              <div className="col-span-2 flex items-center justify-between">
                <span
                  className={cn(
                    "inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium",
                    contract.status === 'active' ? 'bg-green-100 text-green-800' :
                    contract.status === 'inactive' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  )}
                >
                  {contract.status.charAt(0).toUpperCase() + contract.status.slice(1)}
                </span>
                <a
                  href={contract.contractUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cn(
                    "text-gray-400 hover:text-gray-500",
                    hoveredContract === contract.id && "text-indigo-600 hover:text-indigo-700"
                  )}
                  onClick={(e) => e.stopPropagation()}
                >
                  <ExternalLink className="h-5 w-5" />
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}